import { useQuery } from "@tanstack/react-query";

import { ProductType } from "@fitness-app/data-models/entities/Product";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";

import { supabase } from "~/store/initializeStore";

/**
 * A client-service package bought while another one is still assigned to the
 * trainee. The stacking automation keeps the trainee on the current package and
 * re-attaches them to the new one only when the current access ends, so until
 * then the purchase is invisible on the profile card — support kept reading
 * "no renewal" off clients who had already paid (TRE-1899).
 *
 * Reads `product_client` only (no join to the product catalog — trainers can't
 * read it); the product name comes from the access period snapshot.
 */
export const usePendingClientPackage = (email: string | null | undefined, currentProductId: string | undefined) =>
  useQuery({
    queryKey: ["pendingClientPackage", email ?? null, currentProductId ?? null],
    enabled: !!email && !!currentProductId,
    staleTime: 30_000,
    queryFn: async (): Promise<{ pendingPackage: ProductClient; activationAt: number | null } | null> => {
      const { data, error } = await supabase
        .from("product_client")
        .select('id, productId, productType, status, createdAt, accessPeriod, productDuration, scheduledAccessPeriod')
        .eq("email", email!)
        .eq("status", "active")
        .in("productType", [ProductType.PERSONAL_CLIENT_SERVICE, ProductType.AUTOMATED_CLIENT_SERVICE]);

      if (error) {
        throw new Error(error.message);
      }

      const rows = (data ?? []) as unknown as ProductClient[];
      const assigned = rows.find((row) => row.productId === currentProductId);

      if (!assigned) {
        return null;
      }

      // A queued package has not started on its own yet — the stacking automation
      // attaches it at the switch. A row whose period already runs is a parallel
      // purchase (e.g. a same-checkout companion product), not a renewal (TRE-1916).
      const nowUnix = Math.floor(Date.now() / 1000);
      const hasStarted = (row: ProductClient) => {
        if (row.productDuration?.startAt) {
          return row.productDuration.startAt <= nowUnix;
        }
        if (row.accessPeriod?.currentPeriodStart) {
          return row.accessPeriod.currentPeriodStart <= nowUnix;
        }
        return false;
      };

      // An installment plan stacked behind the current package starts BILLING at
      // checkout even though its app activation is deferred, so `hasStarted`
      // mistook it for a parallel purchase and the card stayed empty — support
      // kept reading "no renewal" off clients who had already paid (TRE-1989).
      // The stacking reschedule marks such plans when it credits the deferred
      // days, and that marker is the signal that the row is a queued package.
      const isDeferredActivation = (row: ProductClient) =>
        "scheduledAccessPeriod" in row &&
        Boolean(
          row.scheduledAccessPeriod?.some(
            (period) =>
              period.type === "free_extension" && period.metadata?.extensionType === "stacking_deferred_activation",
          ),
        );

      const pendingPackage = rows
        .filter(
          (row) =>
            row.productId !== currentProductId &&
            new Date(row.createdAt) > new Date(assigned.createdAt) &&
            (!hasStarted(row) || isDeferredActivation(row)),
        )
        .sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt))[0];

      if (!pendingPackage) {
        return null;
      }

      const activationAt =
        assigned.productDuration?.endAt ||
        assigned.accessPeriod?.currentPeriodEnd ||
        (assigned.accessPeriod && "endDate" in assigned.accessPeriod ? assigned.accessPeriod.endDate : null) ||
        null;

      return { pendingPackage, activationAt };
    },
  });
